import { Alert, Box, Grid, IconButton, Link, Typography, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomIcon from "components/CustomIcon";
import Status from "components/Status";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  cleanAllNutritionData,
  cloneNutritionProgram,
  getNutritions,
} from "store/actions/nutritions";
import { formatDate } from "utils/common";
import { THUMBNAIL_PLACEHOLDER_GRAY } from "utils/constants";

const StyledAlert = styled(Alert)(() => ({
  cursor: "pointer",
  border: "1px solid #E0E4EA",
  ".MuiAlert-icon": {
    display: "none",
  },
  ".MuiAlert-action": {
    alignItems: "center",
  },
  ".MuiAlert-message": {
    width: "100%",
    display: "flex",
    alignItems: "center",
    color: "#4F5668",
    fontSize: "14px",
    padding: 0,
  },
}));

function checkAndAddCopy(str) {
  if (str.includes("copy")) {
    const regex = /copy\s*(\d*)$/;
    const match = str.match(regex);
    let count = 1;
    if (match && match[1]) {
      count = parseInt(match[1], 10);
    }
    str = str.replace(regex, "");

    return str.trim() + " copie " + (count + 1);
  } else {
    return str.trim() + " copie";
  }
}

const useStyle = makeStyles(() => ({
  img: {
    height: "40px",
    width: "40px",
    borderRadius: "8px",
  },
}));

const NutritionItem = (props) => {
  const { toggleDeleteModel, nutrition, isTemplate, isEvocoachTemplate } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyle();
  const fullName = (fName, lName) => {
    if (fName || lName) {
      return `${fName} ${lName}`;
    } else {
      return "-";
    }
  };
  const actionButtons = (
    <>
      <IconButton
        color="inherit"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(cleanAllNutritionData());
          navigate(`/nutrition/programs/${nutrition["_id"]}`);
        }}
      >
        <CustomIcon
          iconSrc="/assets/logo/detail_icon.svg"
          iconHoverSrc="/assets/logo/detail_icon_hover.svg"
        />
      </IconButton>
      <IconButton
        color="inherit"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          dispatch(cloneNutritionProgram(nutrition._id)).then((newId) => {
            if (newId && isEvocoachTemplate) {
              navigate(`/nutrition/programs/${newId}`);
            } else {
              dispatch(getNutritions());
            }
          });
        }}
      >
        <CustomIcon
          iconSrc="/assets/logo/copy.svg"
          iconHoverSrc="/assets/logo/copy_hover.svg"
        />
      </IconButton>
      {!isTemplate && (
        <IconButton
          color="info.main"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleDeleteModel(nutrition);
          }}
        >
          <CustomIcon
            iconSrc="/assets/logo/trash_icon.svg"
            iconHoverSrc="/assets/logo/trash_icon_hover.svg"
          />
        </IconButton>
      )}
    </>
  );

  return (
    <StyledAlert
      variant="outlined"
      severity="info"
      onClick={() => {
        dispatch(cleanAllNutritionData());
        navigate(`/nutrition/programs/${nutrition["_id"]}`);
      }}
    >
      {/* Mobile card layout */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, width: "100%", gap: 1.5, alignItems: "flex-start" }}>
        <img
          src={nutrition.thumbnail}
          alt="profile"
          className={classes.img}
          onError={(event) => {
            event.target.src = THUMBNAIL_PLACEHOLDER_GRAY;
            event.onerror = null;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            underline="hover"
            color="#4F5668"
            sx={{ cursor: "pointer", display: "block", fontWeight: 600, mb: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(cleanAllNutritionData());
              navigate(`/nutrition/programs/${nutrition["_id"]}`);
            }}
          >
            {nutrition.name}
          </Link>
          <Typography fontSize="12px" color="#757E97" noWrap>
            {fullName(nutrition.first_name, nutrition.last_name)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.75 }}>
            <Status type={nutrition.status} label={nutrition.status} />
            <Box sx={{ display: "flex" }}>{actionButtons}</Box>
          </Box>
        </Box>
      </Box>

      {/* Desktop row layout */}
      <Grid container sx={{ display: { xs: "none", sm: "flex" } }}>
        <Grid
          item
          sm={4}
          sx={{ display: "flex", gap: "10px", alignItems: "center" }}
        >
          <img
            src={nutrition.thumbnail}
            alt="profile"
            className={classes.img}
            onError={(event) => {
              event.target.src = THUMBNAIL_PLACEHOLDER_GRAY;
              event.onerror = null;
            }}
          />
          <Link
            underline="hover"
            color="#4F5668"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              dispatch(cleanAllNutritionData());
              navigate(`/nutrition/programs/${nutrition["_id"]}`);
            }}
          >
            {nutrition.name}
          </Link>
        </Grid>
        <Grid item sm={2} sx={{ display: "flex", alignItems: "center" }}>
          <Link
            underline="hover"
            color="#4F5668"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              if (nutrition.user_id) {
                navigate(`/clients/${nutrition.user_id}`);
              }
            }}
          >
            {fullName(nutrition.first_name, nutrition.last_name)}
          </Link>
        </Grid>
        {nutrition?.status === "Brouillon" || nutrition?.status === "Modèle" ? (
          <Grid item sm={3} sx={{ display: "flex", alignItems: "center" }}>
            -
          </Grid>
        ) : (
          <Grid item sm={3} sx={{ display: "flex", alignItems: "center" }}>
            {`${formatDate(nutrition.start_date) || ""} - ${formatDate(nutrition.end_date) || ""}`}
          </Grid>
        )}
        <Grid item sm={1} sx={{ display: "flex", alignItems: "center" }}>
          <Status type={nutrition.status} label={nutrition.status} />
        </Grid>
        <Grid item sm={2} sx={{ display: "flex", justifyContent: "right" }}>
          {actionButtons}
        </Grid>
      </Grid>
    </StyledAlert>
  );
};

export default NutritionItem;
